// src/services/userPreferencesService.js

import { db } from '../firebase/firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  onSnapshot
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

/** Default dashboard tile order/titles (ProjectDashboard, DashboardSetup, BrandingSandbox). */
export const DEFAULT_DASHBOARD_PANELS = [
  { title: 'Activity', description: 'Track project activities and updates', key: 'activity' },
  { title: 'Attendance', description: 'Monitor team attendance and GPS check-ins', key: 'attendance' },
  { title: 'Programme', description: 'Manage your project timeline here', key: 'gantt' },
  { title: 'Design', description: 'Access design files and drawings', key: 'design' },
  { title: 'Documents', description: 'Manage project documents and signing', key: 'documents' },
  { title: 'Snags', description: 'Track and resolve project issues', key: 'snag' },
];

const DEFAULT_PANELS = DEFAULT_DASHBOARD_PANELS;

class UserPreferencesService {
  constructor() {
    this.auth = getAuth();
    this.currentUserId = null;
    this.dashboardLayoutUnsubscribe = null;
    this.listeners = new Set();
    this.currentLayout = DEFAULT_PANELS; // Store current layout in memory
    
    // Listen for auth state changes
    this.auth.onAuthStateChanged((user) => {
      if (user && user.uid !== this.currentUserId) {
        this.currentUserId = user.uid;
        this.startListening();
      } else if (!user) {
        this.currentUserId = null;
        this.stopListening();
      }
    });
  }

  // Start listening for dashboard layout changes
  startListening() {
    if (!this.currentUserId || this.dashboardLayoutUnsubscribe) {
      return;
    }

    console.log('🔔 Starting dashboard layout listener for user:', this.currentUserId);
    
    const userPrefsRef = doc(db, 'users', this.currentUserId, 'preferences', 'dashboardLayout');
    
    this.dashboardLayoutUnsubscribe = onSnapshot(userPrefsRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const panels = data.panels || DEFAULT_PANELS;
        this.currentLayout = panels; // Update stored layout
        console.log('📱 Dashboard layout updated:', panels.length, 'panels');
        this.notifyListeners(panels);
      } else {
        this.currentLayout = DEFAULT_PANELS; // Update stored layout
        console.log('📋 No custom dashboard layout found, using defaults');
        this.notifyListeners(DEFAULT_PANELS);
      }
    }, (error) => {
      console.error('❌ Error listening to dashboard layout:', error);
      // Fallback to defaults on error
      this.currentLayout = DEFAULT_PANELS;
      this.notifyListeners(DEFAULT_PANELS);
    });
  }

  // Stop listening for changes
  stopListening() {
    if (this.dashboardLayoutUnsubscribe) {
      this.dashboardLayoutUnsubscribe();
      this.dashboardLayoutUnsubscribe = null;
      console.log('🔇 Stopped dashboard layout listener');
    }
  }

  // Get current dashboard layout (synchronous, returns cached or default)
  getDashboardLayout() {
    return this.currentLayout;
  }

  // Add listener for real-time dashboard layout updates
  addListener(callback) {
    this.listeners.add(callback);
    
    // Immediately call with current layout
    callback(this.currentLayout);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  // Notify all listeners of layout changes
  notifyListeners(panels) {
    this.listeners.forEach(callback => {
      try {
        callback(panels);
      } catch (error) {
        console.error('❌ Error in dashboard layout listener callback:', error);
      }
    });
  }

  // Save dashboard layout to Firestore
  async saveDashboardLayout(panels) {
    if (!this.currentUserId) {
      throw new Error('No user authenticated');
    }

    try {
      const userPrefsRef = doc(db, 'users', this.currentUserId, 'preferences', 'dashboardLayout');
      
      await setDoc(userPrefsRef, {
        panels: panels,
        updatedAt: new Date(),
        version: '1.0'
      }, { merge: true });

      console.log('✅ Dashboard layout saved successfully');
      return true;
    } catch (error) {
      console.error('❌ Error saving dashboard layout:', error);
      throw error;
    }
  }

  // Save map type preference
  async saveMapType(mapType) {
    if (!this.currentUserId) {
      throw new Error('No user authenticated');
    }

    try {
      const userPrefsRef = doc(db, 'users', this.currentUserId, 'preferences', 'mapSettings');
      
      await setDoc(userPrefsRef, {
        mapType: mapType,
        updatedAt: new Date()
      });

      console.log('✅ Map type preference saved successfully');
      return true;
    } catch (error) {
      console.error('❌ Error saving map type preference:', error);
      throw error;
    }
  }

  // Load map type preference
  async loadMapType() {
    if (!this.currentUserId) {
      return 'satellite'; // Default to satellite
    }

    try {
      const userPrefsRef = doc(db, 'users', this.currentUserId, 'preferences', 'mapSettings');
      const userPrefsSnap = await getDoc(userPrefsRef);
      
      if (userPrefsSnap.exists()) {
        const mapType = userPrefsSnap.data().mapType;
        if (mapType && ['satellite', 'roadmap'].includes(mapType)) {
          console.log('✅ Loaded map type preference:', mapType);
          return mapType;
        }
      }
      
      console.log('📋 Using default map type: satellite');
      return 'satellite'; // Default to satellite
    } catch (error) {
      console.error('❌ Error loading map type preference:', error);
      return 'satellite';
    }
  }

  // Load dashboard layout from Firestore
  async loadDashboardLayout() {
    if (!this.currentUserId) {
      return DEFAULT_PANELS;
    }

    try {
      const userPrefsRef = doc(db, 'users', this.currentUserId, 'preferences', 'dashboardLayout');
      const userPrefsSnap = await getDoc(userPrefsRef);
      
      if (userPrefsSnap.exists()) {
        const customLayout = userPrefsSnap.data().panels;
        if (customLayout && customLayout.length > 0) {
          console.log('✅ Loaded custom dashboard layout');
          return customLayout;
        }
      }
      
      console.log('📋 Using default dashboard layout');
      return DEFAULT_PANELS;
    } catch (error) {
      console.error('❌ Error loading dashboard layout:', error);
      return DEFAULT_PANELS;
    }
  }

  // Reset dashboard layout to defaults
  async resetToDefault() {
    return this.saveDashboardLayout(DEFAULT_PANELS);
  }

  // Check if user has custom layout
  async hasCustomLayout() {
    if (!this.currentUserId) {
      return false;
    }

    try {
      const userPrefsRef = doc(db, 'users', this.currentUserId, 'preferences', 'dashboardLayout');
      const userPrefsSnap = await getDoc(userPrefsRef);
      
      if (userPrefsSnap.exists()) {
        const customLayout = userPrefsSnap.data().panels;
        return customLayout && customLayout.length > 0;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Error checking custom layout:', error);
      return false;
    }
  }

  // Cleanup method
  cleanup() {
    this.stopListening();
    this.listeners.clear();
  }
}

// Export singleton instance
export const userPreferencesService = new UserPreferencesService();
export default userPreferencesService;
